import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { observer } from 'mobx-react';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import styles from './view-campaign-segmentations.module.scss';
import { Card } from '@monorepo/base/src/components/card/card';
import { SelectOptions } from '@monorepo/base/src/components/select-new/select-new';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { DropdownNew } from '@monorepo/base/src/components/dropdown-new/dropdown-new';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { useEffect, useMemo } from 'react';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { BarLoader } from '@monorepo/base/src/components/bar-loader/bar-loader';
import { useList } from '../../../modules/hooks/tools/use-list';
import { WebappsCidModel } from '../../../modules/models/webapps-cid.model';

const toWebappsCidOptions = (cids: WebappsCidModel[]): SelectOptions[] =>
	cids.map(item => ({
		label: `${item.getCid()} (${item.getName() || 'Unknown'})`,
		value: item.getCid(),
	}));

const getCidLabel = (cids: WebappsCidModel[], cid?: id) => {
	if (!cid) {
		return '';
	}
	const match = cids.find(item => String(item.getCid()) === String(cid));
	return match ? `${match.getCid()} (${match.getName() || 'Unknown'})` : String(cid);
};

export const ToolsViewCampaignSegmentations = observer(() => {
	const { campaignSegmentsStore, webappsCidStore } = useStores<IAdminxStore>();
	const campaignSegmentsCrudStore = campaignSegmentsStore.getCrud();
	const isLoading = campaignSegmentsCrudStore.getIsLoading();
	const error = campaignSegmentsCrudStore.getHttpError();

	useList<WebappsCidModel>(webappsCidStore.listStore);

	const webappsCidOptions = useMemo(
		() => toWebappsCidOptions(webappsCidStore.listStore.getList()),
		[webappsCidStore.listStore.getList()]
	);

	const selectedCid = campaignSegmentsCrudStore.getData().getCid();
	const segmentations = campaignSegmentsCrudStore.getData().getSegmentations();
	const { dispatchLog } = useLogs();

	useEffect(() => {
		campaignSegmentsCrudStore.reset();
		if (error) {
			dispatchLog({
				msg: `Couldn't fetch segments`,
				type: DispatchLogsTypes.Error,
			});
		}
	}, [error]);

	const onSelectCid = (cidValues: id[]) => {
		campaignSegmentsCrudStore.getData().setCid(cidValues[0]);
		campaignSegmentsCrudStore.getData().setSegmentations([]);
		if (cidValues[0]) {
			campaignSegmentsCrudStore.getById(cidValues[0]);
		}
	};

	const renderSegments = () => {
		if (!selectedCid) {
			return null;
		}

		if (segmentations?.length === 0 && !isLoading) {
			return <Card title={'No Segmentations Found'}> </Card>;
		}

		return segmentations?.map((segment, index) => {
			return (
				<Card key={index} title={'View Webapps CID Segment'} icon={'announcement-01'}>
					<DropdownNew
						label={'Select Segment'}
						classNames={{
							dropdownWrapper: styles.dropdownWrapper,
							parentSelectWrapper: styles.parentSelectWrapper,
							dropdownInput: styles.dropdownInput,
						}}
						options={[
							{
								label: segment.getAuctionPackageName() || '',
								value: segment.getAuctionPackageId() || '',
							},
						]}
						defaultValues={segment.auction_package_id ? [segment.auction_package_id] : ([] as id[])}
						multi={false}
						disabled
					/>
					<div>
						<Input
							className={styles.inputStyle}
							disabled
							value={getCidLabel(webappsCidStore.listStore.getList(), segment.getTargetCid())}
							placeholder={'Target CID'}
						/>
					</div>
				</Card>
			);
		});
	};

	return (
		<Page>
			<div className={styles.formWrapper}>
				<div className={styles.pagebar}>
					<Pagebar>
						<PagebarTitle>View Segmentation</PagebarTitle>
					</Pagebar>
				</div>

				<div className={styles.segmentation}>
					<Card title={'Select CID'} icon={'announcement-01'}>
						<DropdownNew
							classNames={{ dropdownWrapper: styles.cidDropdownWrapper, parentSelectWrapper: styles.parentSelectWrapper }}
							options={webappsCidOptions}
							defaultValues={selectedCid ? [selectedCid] : ([] as id[])}
							autocomplete={true}
							autocompletePlaceholder={'Search CID'}
							required
							onSelect={onSelectCid}
							disabled={webappsCidStore.listStore.getIsLoading() || isLoading}
						/>
						<div className={styles.limitLoaderSize}>
							<BarLoader is={webappsCidStore.listStore.getIsLoading() || isLoading} />
						</div>
					</Card>
					{renderSegments()}
				</div>
			</div>
		</Page>
	);
});
